import { ApolloError, QueryResult, useQuery } from '@apollo/client'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import { InlineErrorBoundary } from '../../../../common/src/components/InlineErrorBoundary'
import { TasksTableMultiselect } from '../../../../common/src/components/TasksTableMultiselect'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import {
  AlignRight,
  BodyTextSpan,
  EllipsisDiv,
  H1,
  H3,
  H5,
  InputLarge,
  IntroText,
  Spacer,
} from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { fetchFirmTasksQuery } from '../../../../common/src/data/queries'
import { camelToTitleCase } from '../../../../common/src/format'
import {
  BulkUpdateOp,
  FetchFirmTasksQuery,
  FetchFirmTasksQueryVariables,
  ShowingPolicy,
  TourType,
  UpdateTasksMutation,
} from '../../../../common/src/types.gen'
import validate, { ValidatorResp } from '../../../../common/src/validate'
import { bulkUpdateTasks } from '../../graphql/mutations'
import { getTasksNotUpdated, renderFieldError, renderTasksList } from './BulkUpdatePage'

enum Policy {
  ACCEPT_SECTION_8 = 'acceptSection8',
  SECTION_8_TEXT = 'section8Text',
  QUALIFICATION_TEXT = 'qualificationText',
  PET_POLICY_TEXT = 'petPolicyText',
  MOVE_IN_COST_TEXT = 'moveInCostText',
  DISCOUNTS_TEXT = 'discountsText',
  APPLICATION_DOCS_TEXT = 'applicationDocsText',
  LEASE_TERMS_TEXT = 'leaseTermsText',
  COSIGNER_POLICY_TEXT = 'cosignerPolicyText',
  REALTOR_TEXT = 'realtorText',
  OFFICE_PHONE = 'officePhone',
  TOUR_TYPES = 'tourTypes',
  AMENITIES_TEXT = 'amenitiesText',
  LAUNDRY_TEXT = 'laundryText',
  UTILITIES_TEXT = 'utilitiesText',
  PARKING_TEXT = 'parkingText',
  HANDICAPPED_UNITS_TEXT = 'handicappedUnitsText',
  MEETING_DIRECTIONS = 'meetingDirections',
  SELF_GUIDED_MEETING_DIRECTIONS = 'selfGuidedMeetingDirections',
  APPLICATION_URL = 'applicationUrl',
  PROPERTY_WEBSITE = 'propertyWebsite',
  FLOORPLANS_URL = 'floorPlansUrl',
  SHOWING_POLICY = 'showingPolicy',
  HOLIDAYS = 'holidays',
}

interface PolicyInfo {
  validator?: (
    s: string
  ) => ValidatorResp<string> | ValidatorResp<boolean> | ValidatorResp<number> | ValidatorResp<TourType[]>
  errorMessage?: string
  placeholder?: string
}

const policyInfos = new Map<Policy, PolicyInfo>([
  [
    Policy.ACCEPT_SECTION_8,
    {
      validator: (s: string) => validate.options({ yes: true, no: false }).validate(s.toLowerCase()),
      errorMessage: 'must be "yes" or "no"',
      placeholder: 'yes',
    },
  ],
  [
    Policy.OFFICE_PHONE,
    {
      validator: (s: string) => validate.phone().validate(s),
      errorMessage: 'must be a valid phone numberr',
      placeholder: '(213) 434-2341',
    },
  ],
  [
    Policy.TOUR_TYPES,
    {
      validator: (s: string) => validate.tourType().validate(s.replace(/\s/g, '').toUpperCase()),
      placeholder: 'IN_PERSON,VIRTUAL',
    },
  ],
  [
    Policy.APPLICATION_URL,
    {
      validator: (s: string) => validate.url().validate(s),
      errorMessage: 'must be a valid url', // eslint-disable-line
      placeholder: 'https://apply-here.com',
    },
  ],
  [
    Policy.PROPERTY_WEBSITE,
    {
      validator: (s: string) => validate.url().validate(s),
      errorMessage: 'must be a valid url',
      placeholder: 'https://life-view-apartments.com',
    },
  ],
  [
    Policy.FLOORPLANS_URL,
    {
      validator: (s: string) => validate.url().validate(s),
      errorMessage: 'must be a valid url',
      placeholder: 'https://life-view-apartments-1br.com',
    },
  ],
  [
    Policy.SHOWING_POLICY,
    {
      validator: (s: string) => validate.options(ShowingPolicy).validate(s.toUpperCase()),
      placeholder: 'NOTICE',
    },
  ],
])

interface PoliciesSectionProps {
  firmId: number
  onUpdate: () => void
}

interface PoliciesImplProps {
  onUpdate: () => void
}

type LeasingTask = UpdateTasksMutation['bulkUpdateTasks'][0]

interface PoliciesSectionState {
  selectedPolicy?: Policy
  selectedTaskIds: Set<string | number>
  errorPolicy: string
  errorPolicyText: string
  errorTaskIds: string
  submitting: boolean
  saveSuccess: boolean
  updatedTasks: LeasingTask[]
}

type PoliciesSectionImplProps = PoliciesImplProps &
  FetchFirmTasksQueryVariables &
  QueryResult<FetchFirmTasksQuery, FetchFirmTasksQueryVariables>

class PoliciesSectionImpl extends React.PureComponent<PoliciesSectionImplProps, PoliciesSectionState> {
  constructor(props: PoliciesSectionImplProps) {
    super(props)

    this.state = {
      selectedPolicy: undefined,
      selectedTaskIds: new Set<string | number>(),
      errorPolicy: '',
      errorPolicyText: '',
      errorTaskIds: '',
      submitting: false,
      saveSuccess: false,
      updatedTasks: [] as LeasingTask[],
    }
  }

  private policyTextRef = React.createRef<HTMLInputElement>()

  private onSelectedTaskIdsChange = (selectedTaskIds: Set<string | number>) => this.setState({ selectedTaskIds })

  private policyLoad = (prefix: string) => {
    const policies = Object.keys(Policy)
    const matchPolicies = policies.filter(s => s.toLowerCase().startsWith(prefix.toLowerCase())).slice(0, 10)

    const matches = matchPolicies.map(s => Policy[s as keyof typeof Policy])
    return Promise.resolve(matches)
  }

  private policyRow = (policy: Policy) => (
    <IntroText>
      <EllipsisDiv>
        <BodyTextSpan>{camelToTitleCase(policy)}</BodyTextSpan>
      </EllipsisDiv>
    </IntroText>
  )

  private onChoosePolicy = (selectedPolicy: Policy) => {
    this.setState({ selectedPolicy })
  }

  private validateSelectedPolicy = (selectedPolicy: Policy, policyText?: string): ValidatorResp<any> => {
    if (policyText) {
      return this.validatePolicyText(selectedPolicy, policyText)
    } else {
      return { value: policyText }
    }
  }
  private validatePolicyText = (selectedPolicy: Policy, policyText: string): ValidatorResp<any> => {
    if (policyInfos.get(selectedPolicy)?.validator) {
      const validatorResp = policyInfos.get(selectedPolicy)!.validator!(policyText)
      if (validatorResp.error && policyInfos.get(selectedPolicy)?.errorMessage) {
        return { error: new Error(policyInfos.get(selectedPolicy)!.errorMessage) }
      } else {
        return validatorResp
      }
    }

    return { value: policyText }
  }

  private onSaveUpdate = () => {
    const { onUpdate, data } = this.props
    const { selectedPolicy, selectedTaskIds } = this.state

    if (data == null || data.firm == null) {
      return
    }

    this.setState({ submitting: true })

    const policyText = this.policyTextRef.current!.value.trim()

    let errorPolicy = ''
    let errorPolicyText = ''
    let errorTaskIds = ''

    const value = {} as { [key: string]: string | number | boolean | TourType[] | undefined | null }

    if (selectedPolicy) {
      const validatorResp = this.validateSelectedPolicy(selectedPolicy, policyText)

      if (validatorResp.error) {
        errorPolicyText = validatorResp.error.message
      } else {
        errorPolicyText = ''
        value[selectedPolicy.toString()] = validatorResp.value
      }
    } else {
      errorPolicy = 'must select a policy'
    }

    if (selectedTaskIds.size === 0) {
      errorTaskIds = 'must select a property'
    }

    this.setState({ errorPolicy, errorPolicyText, errorTaskIds })

    if (errorPolicy || errorPolicyText || errorTaskIds) {
      this.setState({ submitting: false })
      return
    }

    const taskIds = Array.from(selectedTaskIds).map(id => parseInt(id.toString(), 10))

    bulkUpdateTasks({
      taskIds,
      field: BulkUpdateOp.POLICIES,
      value: JSON.stringify(value),
    })
      .then(res => {
        onUpdate()
        this.setState({ saveSuccess: true, submitting: false, updatedTasks: res.data!.bulkUpdateTasks })
      })
      .catch((err: ApolloError) => {
        this.setState({ submitting: false })
        handleError(err)
      })
  }

  private renderPoliciesForm = () => {
    const { data, firmId } = this.props
    const { selectedPolicy, submitting, errorPolicy, errorPolicyText, errorTaskIds } = this.state

    if (firmId == null) {
      return null
    }

    if (data == null || data.firm == null || data.firm.tasks == null) {
      return null
    }

    return (
      <>
        <H5>Which would you like to update?</H5>
        <Spacer height={16} />
        <TypeaheadBox<Policy>
          loadFunc={this.policyLoad}
          rowFunc={this.policyRow}
          onChoose={this.onChoosePolicy}
          inputTextOnSelect={camelToTitleCase}
          placeholder="Choose policy..."
          showResultsWhenEmpty
          clearOnBlur={false}
          hasError={errorPolicy !== ''}
        />
        {errorPolicy && renderFieldError(errorPolicy)}
        <Spacer height={32} />
        <H5>What’s the new text?</H5>
        <Spacer height={8} />
        <InputLarge
          hasError={errorPolicyText !== ''}
          placeholder={selectedPolicy && policyInfos.get(selectedPolicy)?.placeholder}
          ref={this.policyTextRef}
        />
        {errorPolicyText && renderFieldError(errorPolicyText)}
        <Spacer height={64} />
        <H3>For which properties?</H3>
        <Spacer height={16} />
        <TasksTableMultiselect tasks={data.firm.tasks} onChange={this.onSelectedTaskIdsChange} />
        {errorTaskIds && renderFieldError(errorTaskIds)}
        <Spacer height={32} />
        <AlignRight>
          <Button disabled={submitting} onClick={this.onSaveUpdate}>
            Save Update
          </Button>
        </AlignRight>
      </>
    )
  }

  private renderPoliciesSuccess = () => {
    const { data } = this.props
    const { selectedPolicy, updatedTasks, selectedTaskIds } = this.state
    const policyText = this.policyTextRef.current!.value.trim()

    if (data == null || data.firm == null) {
      return null
    }

    const tasksNotUpdated = getTasksNotUpdated(data.firm.tasks, selectedTaskIds, updatedTasks)

    return (
      <>
        <H1>Thank you!</H1>
        <Spacer height={64} />
        {updatedTasks.length > 0 && (
          <>
            <H3 color="silver">
              We were able to update `{selectedPolicy}` to `{policyText}` at the following properties:
            </H3>
            <Spacer height={16} />
            {renderTasksList(updatedTasks)}
          </>
        )}
        {tasksNotUpdated.length > 0 && (
          <>
            <H3 color="silver">We were not able to update `{selectedPolicy}` for following properties:</H3>
            <Spacer height={16} />
            {renderTasksList(tasksNotUpdated)}
          </>
        )}
        <Spacer height={16} />
      </>
    )
  }

  render() {
    const { saveSuccess } = this.state

    return saveSuccess ? this.renderPoliciesSuccess() : this.renderPoliciesForm()
  }
}

export default function PoliciesSection(props: PoliciesSectionProps) {
  const { firmId } = props

  if (firmId == null) {
    return null
  }

  const res = useQuery<FetchFirmTasksQuery, FetchFirmTasksQueryVariables>(fetchFirmTasksQuery, {
    variables: { firmId },
  })

  if (res.loading || res.error) {
    return null
  }

  return (
    <InlineErrorBoundary componentName="PoliciesSection">
      <PoliciesSectionImpl {...props} {...res} />
    </InlineErrorBoundary>
  )
}
